import type { FilterField } from '@orbind/ui'
import type { SavedFilter } from '@orbind/core'
import type { CustomField, CustomFieldType } from './custom-field-data.ts'

/**
 * Regras condicionais dos campos complementares (SPEC-20260828-1914, fase 2).
 *
 * Cada campo pode ter `config.visibilityRule` (mostrar quando…) e
 * `config.requiredRule` (exigir quando…), guardados no formato `SavedFilter`
 * (critérios do construtor existente). As condições olham OUTROS campos
 * complementares E campos nativos do item (tipo, status, prioridade, etapa,
 * área, empresa). Avaliação: `evaluateFilter(valores, regra)`.
 */

const tipoParaFiltro = (t: CustomFieldType): FilterField['type'] => {
  switch (t) {
    case 'number':
      return 'number'
    case 'date':
    case 'datetime':
      return 'date'
    case 'boolean':
      return 'boolean'
    case 'select':
    case 'multiselect':
      return 'select'
    default:
      return 'text'
  }
}

/** Campos nativos do item disponíveis nas condições (chave = `sys_*`). */
export const CAMPOS_NATIVOS: { key: string; label: string }[] = [
  { key: 'sys_type', label: 'Tipo do item' },
  { key: 'sys_status', label: 'Status' },
  { key: 'sys_priority', label: 'Prioridade' },
  { key: 'sys_stage', label: 'Etapa' },
  { key: 'sys_area', label: 'Área' },
  { key: 'sys_company', label: 'Empresa' },
]

/** Lista de campos para o construtor de critérios: complementares + nativos. */
export function camposParaCriterios(
  campos: CustomField[],
  rotulo: (labels: Record<string, string>, fallback: string) => string,
  excluirId?: string,
): FilterField[] {
  const compl: FilterField[] = campos
    .filter((f) => f.id !== excluirId)
    .map((f) => ({ key: f.key, label: rotulo(f.labels, f.key), type: tipoParaFiltro(f.type) }))
  const nativos: FilterField[] = CAMPOS_NATIVOS.map((n) => ({ key: n.key, label: n.label, type: 'select' as const }))
  return [...compl, ...nativos]
}

/** Valores do item (complementares + nativos) nas chaves usadas pelos critérios. */
export function valoresParaCriterios(
  item: {
    workItemTypeId: string | null
    workItemStatusId: string | null
    workItemPriorityId: string | null
    flowStepId: string | null
    companyAreaId: string | null
    companyId: string | null
  },
  customValues: Record<string, unknown>,
): Record<string, unknown> {
  return {
    ...customValues,
    sys_type: item.workItemTypeId,
    sys_status: item.workItemStatusId,
    sys_priority: item.workItemPriorityId,
    sys_stage: item.flowStepId,
    sys_area: item.companyAreaId,
    sys_company: item.companyId,
  }
}

/** Lê uma regra do config do campo (ou null). */
export function regraDoConfig(config: Record<string, unknown> | undefined, chave: 'visibilityRule' | 'requiredRule') {
  const r = config?.[chave]
  return r && typeof r === 'object' ? (r as SavedFilter) : null
}

/** Regra vazia (sem critérios) — some ao salvar se ficar sem critério. */
export const regraVazia = (nome: string): SavedFilter => ({ id: `rule-${nome}`, name: nome, join: 'AND', criteria: [] })
